import DocumentMeta from "react-document-meta";
import Header from "../../components/header/header";
import FooterComponent from "../../components/footer";
import { Typography } from "@mui/material";
import "./terms-of-service.css";

export default function TermsOfServicePage(): JSX.Element {
  const meta = {
    title: "Terms of Service | 123Skincare",
    canonical: "https://www.123skincare.pro/terms-of-service",
  };

  return (
    <DocumentMeta {...meta}>
      <Header showLogo={true} />
      <div className="main terms-of-service-page">
        <div className="container">
          <div className="wrapper">
            <div><h1>Terms of Service</h1></div>
            <div className="paragraph">
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                <strong>Effective Date:</strong> 22nd October 2024
                <br/><strong>Last Updated:</strong>  22nd October 2024
                <br/><br/>Welcome to <strong>123Skincare</strong>. By accessing and using our website and services, including AI-generated product suggestions (the “Services”), you agree to comply with and be bound by these Terms of Service (“TOS”). Please read these terms carefully before using the Services.
                <br/><br/><span className="italic">If you do not agree to these terms, please do not use the Services.</span>
              </Typography>
            </div>
            <div className="paragraph border-top">
              <Typography variant={"h4"}>1. Overview of Services</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                123Skincare offers a platform that provides personalized skincare product suggestions using artificial intelligence (AI) based on user inputs. The Services are intended to enhance your skincare routine by offering suggestions that match your needs and preferences.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>2. Not Medical Advice</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                All product suggestions provided by 123Skincare's AI are based on general information and user inputs. These suggestions are for <strong>informational purposes only</strong> and are not intended to diagnose, treat, cure, or prevent any skin condition.
                <br/><br/><strong>Important:</strong>
                <ul>
                  <li>The information provided through the Services does not substitute for the advice of a licensed dermatologist or healthcare provider.</li>
                  <li>Always perform a patch test before using new products, and consult a healthcare professional before incorporating suggested products into your routine, especially if you have sensitive skin, known allergies, or existing medical conditions.</li>
                  <li>By using the Services, you acknowledge that 123Skincare is not responsible for any adverse reactions or outcomes from using the suggested products or routines.</li>
                </ul>
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>3. User Responsibility</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                You are solely responsible for ensuring that the skincare products and routines recommended through 123Skincare’s Services are suitable for your skin type, allergies, and any other individual health considerations.
                <br/><br/><strong>Patch Test Reminder:</strong> We strongly advise performing a patch test before using any new skincare product to check for possible allergic reactions or skin sensitivity.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>4. Automated AI Recommendations</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
              The product suggestions provided through 123Skincare are generated by an AI system based on the information you provide (e.g., skin type, age range, concerns). The AI's recommendations involve automated decision-making or profiling to offer personalized results.
              <br/><br/>By using the Services, you consent to the use of AI for personalized product suggestions. You have the right to:
              <ul>
                <li><strong>Request human intervention</strong> if you believe an AI-based recommendation is unsuitable.</li>
              </ul>
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>5. Data Collection and GDPR Compliance</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
              Your privacy is important to us, and we comply with the <strong>General Data Protection Regulation (GDPR)</strong>. We collect and process personal data such as:
              <ul>
                <li><strong>Personal Information:</strong> Details like your name, email, and any information provided about your skin type, skincare preferences, or concerns.</li>
                <li><strong>Usage Information:</strong> Data on how you interact with our AI system and the Services.</li>
              </ul>
              <strong>Legal Basis for Processing:</strong>
              <ul>
                <li><strong>Consent:</strong> We process your personal data based on your explicit consent, which you provide by interacting with our Services and submitting information to the AI system.</li>
                <li><strong>Legitimate Interest:</strong> To improve the Services and offer personalized suggestions.</li>
              </ul>
              You have the right to withdraw consent at any time, request access to your data, request data deletion, or object to certain processing activities. For more details, refer to our Privacy Policy.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>6. Cookies and Tracking Technologies</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                We use cookies and other tracking technologies to enhance user experience. By using our Services, you agree to our use of cookies as described in our Cookie Policy. You can manage your cookie preferences in your browser settings.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>7. Product Liability Disclaimer</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                123Skincare does not manufacture or sell the skincare products that are suggested through the Services. We are not responsible for the quality, safety, or efficacy of any products recommended. Any issues with the suggested products, including adverse reactions, should be addressed directly with the product manufacturer or seller.
                <br/><br/>You acknowledge that 123Skincare:
                <ul>
                  <li>Does <strong>not guarantee</strong> any specific results from using the suggested skincare products.</li>
                  <li>Will not be held liable for any adverse effects resulting from the use of these products.</li>
                </ul>
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>8. Third-Party Links and Affiliations</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                Our Services may include links to third-party websites or products. We may receive an affiliate commission if you purchase products through these links, but 123Skincare does not endorse or guarantee the safety, effectiveness, or quality of these third-party products.
                <br/><br/>You are responsible for reviewing and understanding the terms, conditions, and privacy policies of any third-party websites or products before making any purchases.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>9. User Conduct</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                By using our Services, you agree not to:
                <ul>
                  <li>Provide false or misleading information regarding your skin type or skincare preferences.</li>
                  <li>Use our Services for any unlawful or harmful activities.</li>
                </ul>
                Violation of these terms may result in suspension or termination of your access to the Services.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>10. Limitation of Liability</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                To the maximum extent permitted by applicable law, 123Skincare, its affiliates, and its employees are not liable for any indirect, incidental, special, or consequential damages arising from:
                <ul>
                  <li>The use or inability to use our Services or products suggested through the AI system.</li>
                  <li>Adverse reactions or issues with suggested products.</li>
                  <li>The accuracy, completeness, or reliability of the AI-generated suggestions.</li>
                </ul>
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>11. Data Retention and Security</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                We retain your personal data only for as long as necessary to provide the Services or as required by law. We store data securely on <strong>Amazon Web Services (AWS)</strong> servers with appropriate security measures in place to protect against unauthorized access or data breaches.
                <br/><br/>For more details on how we handle your data, please refer to our Privacy Policy.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>12. Termination of Service</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                We reserve the right to terminate or suspend access to our Services without notice if you violate these Terms or engage in harmful activities.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>13. Changes to the Terms</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                We may update these Terms from time to time. Significant changes will be communicated to users via email or through a notification on our website. Continued use of the Services after such changes means that you accept the updated terms.
              </Typography>
            </div>
            <div className="paragraph">
              <Typography variant={"h4"}>14. Contact Us</Typography>
              <Typography variant={"body1"} sx={{ marginTop: "20px" }}>
                If you have any questions about these Terms, or if you would like to exercise your rights under GDPR, please contact us at: <a href="mailto:hello@123skincare.pro">hello@123skincare.pro</a>
              </Typography>
            </div>
            
            <FooterComponent />
          </div>
        </div>
      </div>
    </DocumentMeta>
  );
}